/**
 * The product page for a listing that is gone: archived, sold elsewhere or
 * deleted. Partner widgets (weatherbug, realgm, kezi) keep linking Vendoo
 * listings for days after Vendoo takes them down, and those readers were sent
 * to the home page, where they left.
 *
 * It says the item is gone and shows live listings like it, in the browse
 * grid. Nothing of the gone listing itself is shown: status 0 also covers
 * drafts, moderation holds, takedowns and banned sellers.
 */
import Link from "next/link";
import { useEffect } from "react";
import Listings from "./legacy-listings";
import { ListingGridSkeleton } from "./search-listing/ListingGridSkeleton";
import { useProduct } from "../contexts";
import { useGoneListingSimilar } from "../hooks/useGoneListingSimilar";

export const goneHeading = "This item is no longer available";
export const goneSubheading = "It may have sold, or the seller took it down.";
export const goneSimilarHeading = "Similar items you can buy now";
export const goneBrowseLabel = "Browse more listings";

interface ListingGoneProps {
  listingId: string;
}

export default function ListingGone({ listingId }: ListingGoneProps) {
  const { setProduct, setSimilarListings } = useProduct();
  const { listings, isLoading } = useGoneListingSimilar(listingId);

  // The flip buttons on the next product page walk this list.
  useEffect(() => {
    if (listings.length > 0) setSimilarListings(listings);
  }, [listings, setSimilarListings]);

  return (
    // `.search-listings` lays its children out in a row; like the store page,
    // everything sits in its one child.
    <section className="search-listings py-8" data-testid="listing-gone">
      <div>
        <div className="mx-auto max-w-xl px-6 pb-8 text-center">
          <h1 className="mb-3">{goneHeading}</h1>
          <p className="text-base">{goneSubheading}</p>
        </div>
        {isLoading ? (
          <ListingGridSkeleton tiles={8} />
        ) : listings.length > 0 ? (
          <>
            <h2 className="browse-heading">{goneSimilarHeading}</h2>
            <div className="listing-cont" data-testid="listing-gone-similar">
              <Listings
                listName="similarList"
                list={listings}
                setProduct={setProduct}
                max={99999}
                pageType="similar"
              />
            </div>
          </>
        ) : null}
        <div className="py-8 text-center">
          <Link href="/" className="button">
            {goneBrowseLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}
